import React, { useContext } from 'react'
import UIEstimatorAvatar from '@paintscout/ui/components/EstimatorAvatar'
import type { EstimatorAvatarProps } from '@paintscout/ui/components/EstimatorAvatar'
import { UsersContext } from '../../providers/UsersProvider/UsersContext'
import { withLiveOwnerName } from '../../util/withLiveOwnerName'

/**
 * `EstimatorAvatar` renders its initials (and its tooltip, and — when no explicit `avatarColor` is
 * given — its generated colour) from `user.firstName`/`user.lastName`. Callers pass the denormalized
 * owner snapshot frozen onto the document, which goes stale the moment a seat is renamed: `owner.id`
 * still points at the right account, but the name copy is the previous person's (#21800).
 *
 * This wrapper resolves the name live from `UsersProvider` before delegating, so every avatar
 * surface is immune to a rename fan-out that was missed, failed, or has not run yet.
 *
 * It reads `UsersContext` directly rather than calling `useUsers()`, which throws outside a
 * provider. Every app mounts `UsersProvider`, but Storybook and unit trees may not, and an avatar
 * must never be the reason a tree fails to render. `withLiveOwnerName` keeps the snapshot when the
 * id is absent from the map, so deactivated users and customer-view behave exactly as before.
 */
export default function EstimatorAvatar(props: EstimatorAvatarProps) {
  // Read directly off the context — see the note above on useUsers() throwing.
  const usersById = useContext(UsersContext)?.usersById

  return <UIEstimatorAvatar {...props} user={withLiveOwnerName(props.user, usersById)} />
}
